import {
  Button,
  Divider,
  HStack,
  IconButton,
  Tooltip,
  InputGroup,
  InputLeftElement,
  NumberInput,
  NumberInputField,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Portal,
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import {
  faCheck,
  faCircleDot,
  faChevronDown,
  faDiamond,
  faDrawPolygon,
  faMagicWandSparkles,
  faTimes,
  faTableList,
} from '@fortawesome/free-solid-svg-icons';
import { useFlag } from '@unleash/proxy-client-react';
import { useTranslation } from 'react-i18next';
import { useHotkeys } from 'react-hotkeys-hook';
import { useCallback, useState, useEffect } from 'react';
import { useQuery } from 'urql';
import {
  getCreateLineInput,
  getCreatePointInput,
  getCreatePolygonInput,
  getCreateDrainLineInput,
  getCreateLegendInput,
} from './mappers/mapDrawingResults';
import { ProtanRoofOutlineSubToolbar } from './ProtanRoofOutlineSubToolbar';
import { getSheetShapeUrn } from 'src/components/viewers/common/hooks/useSheetShapesManager';
import { useSheetViewerV2 } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetViewerV2';
import SparkelIcon from 'src/components/common/icon/SparkelIcon';
import { DrawingMode } from 'src/components/viewers/SheetsViewerV2/autodesk/tools/ShapesDrawingTool';
import { ActiveTool } from 'src/components/viewers/SheetsViewerV2/enums/ActiveTool';
import { ReactComponent as PolylineIcon } from 'src/assets/icons/draw-polyline.svg';
import { ReactComponent as RectangleIcon } from 'src/assets/icons/draw-rectangle.svg';
import { useShapes as useShapesNaming } from 'src/hooks/shapes';
import { ShapeDrawingResultType } from 'src/components/orderContractor/shapes/ShapeDrawing';
import { CreateSheetShapeInput, GetProjectDeepDocument } from 'src/gql/graphql';
import { useUserTenant } from 'src/services/auth-info';
import { Point2 } from 'src/domain/geometry/geometric-types';
import { useOrderLabelsProtan } from 'src/hooks/protan-data';
import { Substrate } from 'src/components/orderContractor/ProductChoices';
import {
  Diamond,
  KileTypeSKU,
} from 'src/components/orderContractor/pdf-shapes/protan/SheetWedgeShapeDrawing';
import { useShapeFolders } from 'src/hooks/shape-folders';

// eslint-disable-next-line complexity
export default function SheetViewerV2ShapesToolbar() {
  const { t } = useTranslation(['project', 'protan']);
  const enableDrainageLine = useFlag('enable-drainage-line');
  const { isProtan } = useUserTenant();
  const {
    substrate,
    fireRequirements,
    energyRequirements,
    location,
    buildingHeight,
    qkast: windLoad,
  } = useOrderLabelsProtan();
  const { selectedFolder } = useShapeFolders();

  // State to track the roof outline sub-mode when in roof outline mode
  const [roofOutlineSubMode, setRoofOutlineSubMode] = useState<
    DrawingMode.Polygon | DrawingMode.Rectangle | DrawingMode.MagicPolygon
  >(DrawingMode.Polygon);

  // State to track roof outline input values
  const [roofOutlineData, setRoofOutlineData] = useState({
    energyRequirements: energyRequirements || '0.13',
    fireRequirements: fireRequirements || 'Combustible',
    substrate: substrate || Substrate.Steel,
    location: location || 'Sør',
    buildingHeight: buildingHeight || '0',
    windLoad: windLoad || '',
  });

  const {
    projectId,
    activeSheet,
    setActiveTool,
    drawing: {
      isDrawing,
      drawingMode,
      setDrawingMode,
      createShape,
      createShapeResult,
      hasResults,
      results,
      updateShape,
      updateShapeResult,
      shapeToEdit,
      drainLineData,
      minHeight,
      setMinHeight,
    },
  } = useSheetViewerV2();
  const { getNewShapeName } = useShapesNaming(projectId);

  // Get orderId from URL to pre-select in legend dropdown
  const { orderId: urlOrderId } = useParams<{ orderId?: string }>();

  // State for legend order selection - initialize with URL orderId if available
  const [selectedLegendOrderId, setSelectedLegendOrderId] = useState<
    string | null
  >(urlOrderId || null);

  // Update selected legend order when URL orderId changes
  useEffect(() => {
    if (urlOrderId && !selectedLegendOrderId) {
      setSelectedLegendOrderId(urlOrderId);
    }
  }, [urlOrderId, selectedLegendOrderId]);

  // Query project orders for legend dropdown
  const [{ data: projectData }] = useQuery({
    query: GetProjectDeepDocument,
    variables: { id: projectId },
    pause: !projectId,
  });

  const availableOrders = projectData?.project?.orders || [];

  // Force dark mode colors for this component
  const backgroundColor = 'gray.800';
  const iconColor = 'gray.50';
  const borderColor = 'gray.700';
  const activeBackgroundColor = 'teal.700';
  const activeIconColor = 'teal.200';

  // Sync the roof outline sub-mode when drawing mode changes to one of the polygon modes
  useEffect(() => {
    if (
      drawingMode === DrawingMode.Polygon ||
      drawingMode === DrawingMode.Rectangle ||
      drawingMode === DrawingMode.MagicPolygon
    ) {
      setRoofOutlineSubMode(drawingMode);
    }
  }, [drawingMode]);

  // Helper functions for better readability
  const isPolygonModeActive = useCallback(() => {
    return (
      drawingMode === DrawingMode.Polygon ||
      drawingMode === DrawingMode.Rectangle ||
      drawingMode === DrawingMode.MagicPolygon
    );
  }, [drawingMode]);

  const renderPointButton = () => {
    if (isProtan) {
      return (
        <Button
          variant={'ghost'}
          colorScheme={'teal'}
          _active={{ backgroundColor: activeBackgroundColor }}
          _hover={{ backgroundColor: 'blackAlpha.100' }}
          size="xs"
          leftIcon={
            <SparkelIcon
              icon={faCircleDot}
              color={
                drawingMode === DrawingMode.Point ? activeIconColor : iconColor
              }
            />
          }
          isActive={drawingMode === DrawingMode.Point}
          onClick={() => setDrawingMode(DrawingMode.Point)}
          color={
            drawingMode === DrawingMode.Point ? activeIconColor : iconColor
          }
        >
          {t('project:shapes.toolbar.point')}
        </Button>
      );
    }

    return (
      <Tooltip label={t('project:shapes.toolbar.point')}>
        <IconButton
          variant={'ghost'}
          colorScheme={'teal'}
          _active={{ backgroundColor: activeBackgroundColor }}
          _hover={{ backgroundColor: 'blackAlpha.100' }}
          aria-label="draw point"
          size="xs"
          icon={
            <SparkelIcon
              icon={faCircleDot}
              color={
                drawingMode === DrawingMode.Point ? activeIconColor : iconColor
              }
            />
          }
          isActive={drawingMode === DrawingMode.Point}
          onClick={() => setDrawingMode(DrawingMode.Point)}
        />
      </Tooltip>
    );
  };

  const renderPolylineButton = () => (
    <Tooltip label={t('project:shapes.toolbar.polyline')}>
      <IconButton
        variant={'ghost'}
        colorScheme={'teal'}
        _active={{ backgroundColor: activeBackgroundColor }}
        _hover={{ backgroundColor: 'blackAlpha.100' }}
        aria-label="draw polyline"
        size="xs"
        icon={
          <SparkelIcon
            as={PolylineIcon}
            color={
              drawingMode === DrawingMode.Polyline ? activeIconColor : iconColor
            }
          />
        }
        isActive={drawingMode === DrawingMode.Polyline}
        onClick={() => setDrawingMode(DrawingMode.Polyline)}
      />
    </Tooltip>
  );

  const renderPolygonButtons = () => (
    <>
      <Tooltip label={t('project:shapes.toolbar.polygon')}>
        <IconButton
          variant={'ghost'}
          colorScheme={'teal'}
          _active={{ backgroundColor: activeBackgroundColor }}
          _hover={{ backgroundColor: 'blackAlpha.100' }}
          aria-label="draw polygon"
          size="xs"
          icon={
            <SparkelIcon
              icon={faDrawPolygon}
              color={
                drawingMode === DrawingMode.Polygon
                  ? activeIconColor
                  : iconColor
              }
            />
          }
          isActive={drawingMode === DrawingMode.Polygon}
          onClick={() => setDrawingMode(DrawingMode.Polygon)}
        />
      </Tooltip>
      <Tooltip label={t('project:shapes.toolbar.magic-polygon')}>
        <IconButton
          variant={'ghost'}
          colorScheme={'teal'}
          _active={{ backgroundColor: activeBackgroundColor }}
          _hover={{ backgroundColor: 'blackAlpha.100' }}
          aria-label="draw magic polygon"
          size="xs"
          icon={
            <SparkelIcon
              icon={faMagicWandSparkles}
              color={
                drawingMode === DrawingMode.MagicPolygon
                  ? activeIconColor
                  : iconColor
              }
            />
          }
          isActive={drawingMode === DrawingMode.MagicPolygon}
          onClick={() => setDrawingMode(DrawingMode.MagicPolygon)}
        />
      </Tooltip>
      <Tooltip label={t('project:shapes.toolbar.rectangle')}>
        <IconButton
          variant={'ghost'}
          colorScheme={'teal'}
          _active={{ backgroundColor: activeBackgroundColor }}
          _hover={{ backgroundColor: 'blackAlpha.100' }}
          aria-label="draw rectangle"
          size="xs"
          icon={
            <SparkelIcon
              as={RectangleIcon}
              color={
                drawingMode === DrawingMode.Rectangle
                  ? activeIconColor
                  : iconColor
              }
            />
          }
          isActive={drawingMode === DrawingMode.Rectangle}
          onClick={() => setDrawingMode(DrawingMode.Rectangle)}
        />
      </Tooltip>
    </>
  );

  // Get the selected order name for the legend dropdown
  const selectedLegendOrderName =
    availableOrders.find((o) => o.id === selectedLegendOrderId)?.name ||
    t('project:shapes.toolbar.select-table');

  const renderLegendButton = () => (
    <>
      <Tooltip label={t('project:shapes.toolbar.legend')}>
        <IconButton
          variant={'ghost'}
          colorScheme={'teal'}
          _active={{ backgroundColor: activeBackgroundColor }}
          _hover={{ backgroundColor: 'blackAlpha.100' }}
          aria-label="add legend"
          size="xs"
          icon={
            <SparkelIcon
              icon={faTableList}
              color={
                drawingMode === DrawingMode.Legend ? activeIconColor : iconColor
              }
            />
          }
          isActive={drawingMode === DrawingMode.Legend}
          onClick={() => setDrawingMode(DrawingMode.Legend)}
        />
      </Tooltip>
      {drawingMode === DrawingMode.Legend && (
        <Menu isLazy>
          <Tooltip label={t('project:shapes.toolbar.legend-table-tooltip')}>
            <MenuButton
              as={Button}
              size="xs"
              borderRadius="md"
              rightIcon={
                <SparkelIcon icon={faChevronDown} size="xs" color="inherit" />
              }
              fontWeight="normal"
              px={3}
              backgroundColor="whiteAlpha.200"
              color="whiteAlpha.900"
              _hover={{ backgroundColor: 'whiteAlpha.300' }}
              _active={{ backgroundColor: 'whiteAlpha.300' }}
            >
              {selectedLegendOrderName}
            </MenuButton>
          </Tooltip>
          <Portal>
            <MenuList
              backgroundColor="gray.700"
              borderColor="gray.600"
              minWidth="200px"
              zIndex={9999}
            >
              {availableOrders.length === 0 ? (
                <MenuItem
                  isDisabled
                  backgroundColor="gray.700"
                  color="gray.400"
                  _hover={{ backgroundColor: 'gray.600' }}
                >
                  {t('project:shapes.toolbar.no-orders')}
                </MenuItem>
              ) : (
                <MenuOptionGroup
                  value={selectedLegendOrderId || ''}
                  type="radio"
                  onChange={(value) =>
                    setSelectedLegendOrderId(value as string)
                  }
                >
                  {availableOrders.map((order) => (
                    <MenuItemOption
                      key={order.id}
                      value={order.id}
                      backgroundColor="gray.700"
                      color="gray.100"
                      _hover={{ backgroundColor: 'gray.600' }}
                      _checked={{
                        backgroundColor: 'gray.600',
                        fontWeight: 'semibold',
                      }}
                    >
                      {order.name}
                    </MenuItemOption>
                  ))}
                </MenuOptionGroup>
              )}
            </MenuList>
          </Portal>
        </Menu>
      )}
    </>
  );

  const renderProtanSpecificButtons = () => (
    <>
      <Button
        variant={'ghost'}
        colorScheme={'teal'}
        _active={{ backgroundColor: activeBackgroundColor }}
        _hover={{ backgroundColor: 'blackAlpha.100' }}
        size="xs"
        leftIcon={
          <SparkelIcon
            icon={faDiamond}
            color={
              drawingMode === DrawingMode.Wedges ? activeIconColor : iconColor
            }
          />
        }
        isActive={drawingMode === DrawingMode.Wedges}
        onClick={() => setDrawingMode(DrawingMode.Wedges)}
        color={drawingMode === DrawingMode.Wedges ? activeIconColor : iconColor}
      >
        {t('project:shapes.toolbar.wedge')}
      </Button>
      {enableDrainageLine && (
        <Button
          variant={'ghost'}
          colorScheme={'teal'}
          _active={{ backgroundColor: activeBackgroundColor }}
          _hover={{ backgroundColor: 'blackAlpha.100' }}
          size="xs"
          leftIcon={
            <SparkelIcon
              icon={faChevronDown}
              color={
                drawingMode === DrawingMode.DrainLine
                  ? activeIconColor
                  : iconColor
              }
            />
          }
          isActive={drawingMode === DrawingMode.DrainLine}
          onClick={() => setDrawingMode(DrawingMode.DrainLine)}
          color={
            drawingMode === DrawingMode.DrainLine ? activeIconColor : iconColor
          }
        >
          Tapered Insulation
        </Button>
      )}
      {enableDrainageLine && drawingMode === DrawingMode.DrainLine && (
        <>
          <Divider orientation="vertical" height={'70%'} />
          <InputGroup size={'xs'} width={148}>
            <InputLeftElement
              width={20}
              color={activeIconColor}
              pointerEvents={'none'}
            >
              Min. height
            </InputLeftElement>
            <NumberInput
              variant={'filled'}
              max={1000}
              min={80} // Stop from setting zero-thickness
              color={iconColor}
              precision={0}
              step={15}
              value={minHeight ? Math.round(minHeight * 1000) : 1000}
              onChange={(value) => {
                const mm = parseFloat(value);
                // First subtract base, then round up to nearest 15mm, then add base back
                const base = substrate === Substrate.Concrete ? 50 : 80;
                const roundedMm =
                  base + Math.ceil(Math.max(0, mm - base) / 15) * 15;
                setMinHeight(roundedMm / 1000); // convert to meters
              }}
              onBlur={(e) => {
                // Enforce the increment rules on blur
                const mm = parseFloat(e.target.value);
                const base = substrate === Substrate.Concrete ? 50 : 80;
                const roundedMm =
                  base + Math.ceil(Math.max(0, mm - base) / 15) * 15;
                // Update the input field with the corrected value
                e.target.value = roundedMm.toString();
                setMinHeight(roundedMm / 1000); // convert to meters
              }}
            >
              <NumberInputField
                paddingLeft={20}
                paddingRight={0}
                borderRadius={'md'}
                autoFocus
                width={'100%'}
                backgroundColor={borderColor}
                _hover={{ backgroundColor: 'blackAlpha.500' }}
              />
            </NumberInput>
            <InputRightElement
              width={10}
              color={'gray.500'}
              pointerEvents={'none'}
            >
              mm
            </InputRightElement>
          </InputGroup>
        </>
      )}
    </>
  );

  const renderActionButtons = () => (
    <>
      <Divider orientation="vertical" height={'70%'} />
      <Tooltip label={t('project:shapes.cancel-drawing')}>
        <IconButton
          aria-label="Cancel drawing"
          colorScheme="gray"
          variant={'ghost'}
          _hover={{ backgroundColor: 'blackAlpha.100' }}
          size="xs"
          onClick={() => setActiveTool(ActiveTool.Default)}
          isRound
          icon={<SparkelIcon icon={faTimes} color={iconColor} />}
        ></IconButton>
      </Tooltip>
      <Tooltip
        label={
          hasResults
            ? t('project:shapes.save-tooltip')
            : t('project:shapes.save-disabled-tooltip')
        }
      >
        <IconButton
          aria-label="Save"
          size="xs"
          onClick={submitDrawing}
          isLoading={createShapeResult.fetching || updateShapeResult.fetching}
          isRound
          isDisabled={
            !hasResults ||
            createShapeResult.fetching ||
            updateShapeResult.fetching
          }
          icon={<SparkelIcon icon={faCheck} color={['gray.50', 'gray.700']} />}
        ></IconButton>
      </Tooltip>
    </>
  );

  // eslint-disable-next-line complexity
  const submitDrawing = useCallback(async () => {
    if (!hasResults || !activeSheet || !activeSheet.data || !results) return;

    const sheetId = activeSheet.data.id;
    const pageNumber = activeSheet.data.page;
    const shapeName = (type: ShapeDrawingResultType) =>
      getNewShapeName('', type);

    let shapeInputs: CreateSheetShapeInput[] | CreateSheetShapeInput | null =
      null;

    // Store wedge data for later use
    let wedgeDiamonds: Diamond[] = [];
    let wedgeKileTypes: KileTypeSKU[] = [];
    let totalWedgeProductLength = 0;

    switch (drawingMode) {
      case DrawingMode.Polygon:
      case DrawingMode.MagicPolygon:
      case DrawingMode.Rectangle:
      case DrawingMode.RoofOutline:
        if (!('multipolygon' in results) || !results.valid) return;
        shapeInputs = getCreatePolygonInput(
          projectId,
          sheetId,
          pageNumber,
          shapeName(ShapeDrawingResultType.Polygon),
          results.multipolygon,
          selectedFolder
        );
        break;

      case DrawingMode.Polyline:
        if (!Array.isArray(results)) return;
        shapeInputs = results
          .filter((r) => r.valid)
          .map((r) => {
            // Type guard to ensure we have the right type
            if ('points' in r && Array.isArray(r.points)) {
              return getCreateLineInput(
                projectId,
                sheetId,
                pageNumber,
                shapeName(ShapeDrawingResultType.LineString),
                r.points as Point2[],
                selectedFolder
              );
            }
            return null;
          })
          .filter(
            (input): input is NonNullable<typeof input> => input !== null
          );
        break;

      case DrawingMode.Point:
        if (!Array.isArray(results)) return;
        shapeInputs = results
          .filter((r) => r.valid)
          .flatMap((r) => {
            // Type guard to ensure we have the right type
            if ('points' in r && Array.isArray(r.points)) {
              return getCreatePointInput(
                projectId,
                sheetId,
                pageNumber,
                shapeName(ShapeDrawingResultType.Point),
                r.points as Point2[],
                selectedFolder
              );
            }
            return [];
          });
        break;

      case DrawingMode.Wedges: {
        if (!Array.isArray(results)) return;
        // For wedges, we need to handle them as wedge-specific shapes
        // The results should have diamond and kileType data

        const wedgeResults = results.filter(
          (r) =>
            r.valid &&
            'type' in r &&
            r.type === 'Wedge' &&
            Array.isArray(r.points) &&
            r.points.length >= 2 &&
            r.points.every(
              (pt) =>
                Array.isArray(pt) &&
                pt.length === 2 &&
                typeof pt[0] === 'number' &&
                typeof pt[1] === 'number'
            )
        );

        if (wedgeResults.length > 0) {
          const wedgeResult = wedgeResults[0];

          // Store the wedge data in outer scope variables
          wedgeDiamonds =
            (
              wedgeResult as {
                wedgeData?: { diamonds: Diamond[]; kileTypes: KileTypeSKU[] };
              }
            ).wedgeData?.diamonds || [];
          wedgeKileTypes =
            (
              wedgeResult as {
                wedgeData?: { diamonds: Diamond[]; kileTypes: KileTypeSKU[] };
              }
            ).wedgeData?.kileTypes || [];

          totalWedgeProductLength =
            (
              wedgeResult as {
                wedgeData?: { totalWedgeProductLength: number };
              }
            ).wedgeData?.totalWedgeProductLength || 0;
        }

        shapeInputs = wedgeResults
          .map((r) => ({
            sheetShape: {
              projectId,
              sheetId,
              urn: getSheetShapeUrn(projectId),
              sheetPageNumber: pageNumber,
              name: 'Wedge',
              folder: selectedFolder,
              sheetShapeLine: {
                create: [
                  {
                    points: (r.points as Point2[]).map((point: Point2) => ({
                      x: point[0],
                      y: point[1],
                    })),
                  },
                ],
              },
            },
          }))
          .filter(
            (input): input is NonNullable<typeof input> => input !== null
          );
        break;
      }

      case DrawingMode.DrainLine: {
        if (!Array.isArray(results)) return;
        // For drain lines, we now get a single result with all points
        const validResults = results.filter((r) => r.valid);
        if (validResults.length > 0) {
          // Take the first (and only) result with all the points
          const drainLineResult = validResults[0];

          // If we're editing an existing shape, don't create shape inputs
          // The updateShape function will handle the update
          if (!shapeToEdit) {
            // Convert drain lines to points array for the input function
            const points =
              ('drainLines' in drainLineResult &&
                Array.isArray(drainLineResult.drainLines) &&
                drainLineResult.drainLines.flatMap(
                  (segment: [Point2, Point2]) => [
                    segment[0], // start point
                    segment[1], // end point
                  ]
                )) ||
              ('points' in drainLineResult &&
                Array.isArray(drainLineResult.points) &&
                (drainLineResult.points as Point2[])) ||
              [];

            // Use "Tapered Insulation" for Protan tenants, otherwise use the default naming
            const drainLineShapeName = isProtan
              ? 'Tapered Insulation'
              : shapeName(ShapeDrawingResultType.DrainLine);

            shapeInputs = getCreateDrainLineInput(
              projectId,
              sheetId,
              pageNumber,
              drainLineShapeName,
              points,
              selectedFolder
            );
          }
        }
        break;
      }

      case DrawingMode.Legend: {
        // For legends, we expect a single point result for position
        if (!Array.isArray(results) || results.length === 0) return;
        const firstResult = results[0];
        if (
          !firstResult.valid ||
          !('points' in firstResult) ||
          !Array.isArray(firstResult.points) ||
          firstResult.points.length === 0
        )
          return;

        if (!selectedLegendOrderId) {
          console.error('No order selected for legend');
          return;
        }

        const position = firstResult.points[0] as Point2;
        shapeInputs = getCreateLegendInput(
          projectId,
          sheetId,
          pageNumber,
          'Legend',
          position,
          selectedLegendOrderId,
          selectedFolder
        );
        break;
      }

      default:
        console.error('Unsupported drawing mode:', drawingMode);
        return;
    }

    if (!shapeInputs && !shapeToEdit) return;

    if (shapeToEdit) {
      // When editing an existing Protan roof outline (polygon/rectangle/magic), pass roofData to update
      if (
        isProtan &&
        (drawingMode === DrawingMode.MagicPolygon ||
          drawingMode === DrawingMode.Polygon ||
          drawingMode === DrawingMode.Rectangle)
      ) {
        await updateShape({
          roofData: {
            energy: roofOutlineData.energyRequirements || '0.13',
            fireRequirement: roofOutlineData.fireRequirements || 'Combustible',
            location: roofOutlineData.location || 'Sør',
            substrate:
              (roofOutlineData.substrate as Substrate) || Substrate.Steel,
            roofHeight: Number(roofOutlineData.buildingHeight) || 0,
            windLoad: Number(roofOutlineData.windLoad) || 800,
          },
        });
      } else {
        await updateShape();
      }
    } else if (shapeInputs) {
      if (Array.isArray(shapeInputs)) {
        // For wedges, pass the wedge data to each shape
        if (drawingMode === DrawingMode.Wedges && wedgeDiamonds.length > 0) {
          await Promise.all(
            shapeInputs.map((input) =>
              createShape(input, {
                wedgeData: {
                  diamonds: wedgeDiamonds,
                  kileTypes: wedgeKileTypes,
                  totalWedgeProductLength: totalWedgeProductLength,
                },
              })
            )
          );
        } else {
          await Promise.all(shapeInputs.map((input) => createShape(input)));
        }
      } else {
        // For drain lines, pass the drain line data to update properties
        if (drawingMode === DrawingMode.DrainLine && drainLineData) {
          await createShape(shapeInputs, {
            drainLineData: drainLineData,
          });
        } else if (
          drawingMode === DrawingMode.Wedges &&
          wedgeDiamonds.length > 0
        ) {
          await createShape(shapeInputs, {
            wedgeData: {
              diamonds: wedgeDiamonds,
              kileTypes: wedgeKileTypes,
              totalWedgeProductLength: totalWedgeProductLength,
            },
          });
        } else if (
          isProtan &&
          (drawingMode === DrawingMode.MagicPolygon ||
            drawingMode === DrawingMode.Polygon ||
            drawingMode === DrawingMode.Rectangle)
        ) {
          await createShape(shapeInputs, {
            roofData: {
              energy: roofOutlineData.energyRequirements || '0.13',
              fireRequirement:
                roofOutlineData.fireRequirements || 'Combustible',
              location: roofOutlineData.location || 'Sør',
              substrate:
                (roofOutlineData.substrate as Substrate) || Substrate.Steel,
              roofHeight: Number(roofOutlineData.buildingHeight) || 0,
              windLoad: Number(roofOutlineData.windLoad) || 800,
            },
          });
        } else {
          await createShape(shapeInputs);
        }
      }
    }

    setActiveTool(ActiveTool.Default);
  }, [
    hasResults,
    activeSheet,
    results,
    drawingMode,
    shapeToEdit,
    setActiveTool,
    getNewShapeName,
    projectId,
    selectedFolder,
    selectedLegendOrderId,
    isProtan,
    updateShape,
    createShape,
    drainLineData,
    roofOutlineData.energyRequirements,
    roofOutlineData.fireRequirements,
    roofOutlineData.location,
    roofOutlineData.substrate,
    roofOutlineData.buildingHeight,
    roofOutlineData.windLoad,
  ]);

  useHotkeys('esc', () => setActiveTool(ActiveTool.Default), {
    enabled: isDrawing,
  });
  useHotkeys('enter', () => submitDrawing(), [submitDrawing], {
    enabled:
      isDrawing &&
      hasResults &&
      !createShapeResult.fetching &&
      !updateShapeResult.fetching,
  });
  useHotkeys(
    'space',
    async () => {
      await submitDrawing();
      // Without this, the drawing tool doesn't have time to clear the previous drawing
      setTimeout(() => {
        setActiveTool(ActiveTool.Drawing);
      }, 10);
    },
    {
      enabled:
        isDrawing &&
        hasResults &&
        !createShapeResult.fetching &&
        !updateShapeResult.fetching &&
        !!activeSheet &&
        !!activeSheet.data,
    },
    [submitDrawing, setActiveTool]
  );
  useHotkeys(
    'shift+s',
    () => setActiveTool(ActiveTool.Drawing),
    [setActiveTool],
    {
      enabled: !!activeSheet && !!activeSheet.data,
    }
  );

  if (!isDrawing) {
    return null;
  }

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        colorScheme: 'dark',
      }}
      data-theme="dark"
    >
      {/* Roof outline sub-toolbar - only for Protan customers */}
      {isProtan && isPolygonModeActive() && (
        <ProtanRoofOutlineSubToolbar
          projectId={projectId}
          roofOutlineData={roofOutlineData}
          setRoofOutlineData={setRoofOutlineData}
          isEditing={!!shapeToEdit}
          roofOutlineSubMode={roofOutlineSubMode}
          setRoofOutlineSubMode={setRoofOutlineSubMode}
          setDrawingMode={setDrawingMode}
          fireRequirements={fireRequirements || ''}
          backgroundColor={backgroundColor}
          borderColor={borderColor}
          iconColor={iconColor}
          activeBackgroundColor={activeBackgroundColor}
          activeIconColor={activeIconColor}
          onSubmit={submitDrawing}
          isSaveDisabled={
            !hasResults ||
            createShapeResult.fetching ||
            updateShapeResult.fetching
          }
          isSaveLoading={
            createShapeResult.fetching || updateShapeResult.fetching
          }
        />
      )}

      {/* Main toolbar - hidden when Protan roof outline mode is active */}
      {!(isProtan && isPolygonModeActive()) && (
        <HStack
          backgroundColor={backgroundColor}
          rounded={50}
          px={1}
          height={8}
          borderColor={borderColor}
          borderWidth="1px"
          mb={1}
        >
          {/* Point button - always visible */}
          {renderPointButton()}

          {/* Polyline button - only for non-Protan users */}
          {!isProtan && renderPolylineButton()}

          {/* Drawing mode buttons - only for non-Protan users */}
          {!isProtan && renderPolygonButtons()}

          {/* Legend button - available for all users */}
          {renderLegendButton()}

          {/* Protan-specific buttons */}
          {isProtan && renderProtanSpecificButtons()}

          {/* Action buttons (cancel/save) - always visible */}
          {renderActionButtons()}
        </HStack>
      )}
    </div>
  );
}
